import { useMemo } from 'react';
import { TrainIcon } from '@rsos/assets/icons';
import useHasAccessToNorfolkSouthern from '@rsos/hooks/useHasAccessToNorfolkSouthern';
import {
  useGetNorfolkSouthernRequestsQuery,
  type NorfolkSouthernRequestResponse,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import NorfolkSouthernListItem from '../NorfolkSouthern/NorfolkSouthernListItem';
import NorfolkSouthernView from '../NorfolkSouthern/NorfolkSouthernView';
import {
  getNorfolkSouthernLatestActivityMs,
  getNorfolkSouthernQueueCategory,
} from '../NorfolkSouthern/norfolkSouthernStatusUtils';
import type {
  ResourceRequestAdapter,
  ResourceRequestIngestionMetadata,
  ResourceRequestListItemProps,
  ResourceRequestPickupLocation,
} from './types';

function useNorfolkSouthernResourceList({ skip }: { skip: boolean }): {
  requests: NorfolkSouthernRequestResponse[];
  isFetching: boolean;
} {
  const { data, isFetching } = useGetNorfolkSouthernRequestsQuery(undefined, {
    skip,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const requests = useMemo(() => data ?? [], [data]);
  return { requests, isFetching };
}

/** The train's position when the consist was requested. */
function getNorfolkSouthernPickupLocation(
  request: NorfolkSouthernRequestResponse,
): ResourceRequestPickupLocation | null {
  const { latitude, longitude } = request.request.train.position;
  return { latitude, longitude };
}

/**
 * Consist requests can only be created from Unite; the digital dispatch API
 * has no ingestion-source field to report.
 */
function getNorfolkSouthernIngestionMetadata(): ResourceRequestIngestionMetadata {
  return { ingestionSource: 'unite', requestMethod: null };
}

function NorfolkSouthernQueueListItem({
  request,
  isUnread,
  onClick,
}: ResourceRequestListItemProps<NorfolkSouthernRequestResponse>): JSX.Element {
  return (
    <NorfolkSouthernListItem
      request={request}
      isUnread={isUnread}
      onClick={onClick}
    />
  );
}

export const norfolkSouthernAdapter: ResourceRequestAdapter<NorfolkSouthernRequestResponse> =
  {
    id: 'norfolk_southern',
    title: 'Train Consist',
    source: 'Norfolk Southern',
    description:
      'Request the consist of a train to identify potential hazardous cargo',
    Icon: TrainIcon,
    useEnabled: useHasAccessToNorfolkSouthern,
    useList: useNorfolkSouthernResourceList,
    getId: request => String(request.id),
    getCallerId: request => request.callerId,
    getPickupLocation: getNorfolkSouthernPickupLocation,
    getIngestionMetadata: getNorfolkSouthernIngestionMetadata,
    getQueueInfo: (request, nowMs) => ({
      category: getNorfolkSouthernQueueCategory(request, nowMs),
      activityMs: getNorfolkSouthernLatestActivityMs(request),
    }),
    ListItemComponent: NorfolkSouthernQueueListItem,
    ViewComponent: NorfolkSouthernView,
  };
